import subpixel from './util/canvasSubpixelOffset';

/** Grid handles chores related to rendering lines and ticks */
class Grid {
	/** @param {Panel} panel - the panel to which this grid belongs */
	constructor(panel) {
		/** @type {Panel} */
		this.panel = panel;
	}

	/**
	 * Render vertical grid lines and xaxis ticks
	 * @param {object} dateLabels
	 */
	renderVerticalGrid(dateLabels) {
		const { panel } = this;
		const ctx = panel.rootContext;
		const grid = this.getVerticalGridPositions(dateLabels);

		this.renderVerticalGridLines(ctx, grid.lines);
		this.renderVerticalAltLines(ctx, grid.altLines);
		this.renderVerticalTicks(ctx, grid.lines); // Regular ticks have the same x positions as regular lines
		this.renderVerticalAltTicks(ctx, grid.altTicks);
	}

	/**
	 * Draw regular vertical ticks
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderVerticalTicks(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top + panel.size.height;
		const { color, length, lineDash, width } = style.xAxis.tick;
		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, width);
	}

	/**
	 * Draw regular vertical lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderVerticalGridLines(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top;
		const color =
			style.grid.vertical.color ||
			style.grid.color;
		const extension =
			style.grid.vertical.extension ||
			style.grid.extension;
		const lineDash =
			style.grid.vertical.lineDash ||
			style.grid.lineDash;
		const lineWidth =
			style.grid.vertical.width ||
			style.grid.width;
		// The extension will push the lines past the chart border
		const length = panel.size.height + extension;

		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth);
	}

	/**
	 * Draw alt vertical lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderVerticalAltLines(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top;
		const color =
			style.grid.vertical.alt.color ||
			style.grid.vertical.color ||
			style.grid.color;
		const extension =
			style.grid.vertical.alt.extension ||
			style.grid.vertical.extension ||
			style.grid.extension;
		const lineDash =
			style.grid.vertical.alt.lineDash ||
			style.grid.vertical.lineDash ||
			style.grid.lineDash;
		const lineWidth =
			style.grid.vertical.alt.width ||
			style.grid.vertical.width ||
			style.grid.width;
		// The extension will push the lines past the chart border
		const length = panel.size.height + extension;

		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth);
	}

	/**
	 * Draw alt vertical ticks
	 * @param {rootContext} ctx - the root context for this grid's parent panel
	 * @param {object} data - chart data
	 */
	renderVerticalAltTicks(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top + panel.size.height;
		const color =
			style.xAxis.tick.alt.color ||
			style.xAxis.tick.color;
		const length =
			style.xAxis.tick.alt.length ||
			style.xAxis.tick.length;
		const lineDash =
			style.xAxis.tick.alt.lineDash ||
			style.xAxis.tick.lineDash;
		const lineWidth =
			style.xAxis.tick.alt.width ||
			style.xAxis.tick.width;

		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth);
	}

	/**
	 * Common render method for vertical lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 * @param {number} x0 - Location of 0 on the yAxis for this panel
	 * @param {number} length - Length of lines to render
	 * @param {string} color - Color to use for these lines
	 * @param {string[]} lineDash - Settings to turn on a dashed line
	 * @param {number} lineWidth - Line width to use for these lines
	 */
	renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth) {
		if (
			lineWidth &&
			(
				color &&
				(
					color !== 'transparent' ||
					color !== 'none'
				)
			)
		) {
			ctx.save();
			ctx.beginPath();

			ctx.strokeStyle = color;
			ctx.lineWidth = lineWidth;
			ctx.setLineDash(lineDash.split(/\s*,\s*/));
			data.forEach(x0 => {
				ctx.moveTo(x0, y0);
				ctx.lineTo(x0, y0 + length);
			});
			ctx.stroke();

			ctx.restore();
		}
	}

	/**
	 * Return x positions of lines, alt lines, and alt ticks (which aren't always the same)
	 * @param {object} dateLabels
	 * @returns {object} Returns object with arrays of line, altLine, and altTick Xaxis locations
	 */
	getVerticalGridPositions(dateLabels) {
		const grid = {
			lines: [],
			altLines: [],
			altTicks: []
		};
		const self = this;
		const { panel } = this;
		let y = 0;
		let i = 0;
		let x0 = 0;
		let dayLabel = {};

		// collect lines, ticks, alt grid lines, and alt ticks.
		Object.keys(dateLabels).forEach(row => {
			const el = dateLabels[row];
			if (!el) return true;
			labelLoop: for (i = el.length - 1, x0 = 0; i >= 0; i--) {
				x0 = subpixel.px(panel.xAxis.primaryScale(el[i].index));

				if (row === 'months' && dateLabels.years) {
					// interday alt grid lines

					// check years for same index, if so add to alt collection and don't draw here.
					for (y = 0; y < dateLabels.years.length; y++) {
						if (dateLabels.years[y].index === el[i].index) {
							grid.altLines.push(x0);
							grid.altTicks.push(x0);
							continue labelLoop;
						}
					}
				} else if (/minutes|hours/.test(row) && dateLabels.days) {
					// intraday alt grid lines

					// check days for same index, if so add to alt collection and don't draw here.
					for (y = 0; y < dateLabels.days.length; y++) {
						dayLabel = dateLabels.days[y];

						if (el[i].index === dayLabel.index) {
							grid.altLines.push(x0);
							grid.altTicks.push(x0);
							continue labelLoop;
						} else if (
							self.hasAltLabelIntraday(el[i], el[i - 1], dayLabel)
						) {
							// use x position of the first dayLabel of each new day
							// when narrow collection's step doesn't line up with the day step.
							grid.altLines.push(
								subpixel.px(panel.xAxis.primaryScale(dayLabel.index))
							);
						}
					}
				}

				grid.lines.push(x0);
			}

			// only draw grids for the first applicable row of labels
			// (ex: months, not both months and years)
			if (el.length) return false;
		});
		// removes final alt line and tick within 25px of the right border to prevent appearance of double border lines (CH-3037)
		if (
			(grid.altLines[0] + 20) > (this.panel.size.width + this.panel.size.padding.left)
		) {
			grid.altLines.shift();
			grid.altTicks.shift();
		}

		return grid;
	}

	/**
	 * When the narrow collection's step doesn't align with the day collection's step, use an alternative
	 * way to determine if this qualifies as an alt label.  an example of this is when the narrow labels
	 * are "hours" and have a step of 2 on even-numbered hours, but the first day label is on an odd hour.
	 * @param {object} narrowLabel
	 * @param {object} prevNarrowLabel
	 * @param {object} dayLabel
	 * @returns {boolean}
	 */
	hasAltLabelIntraday(narrowLabel, prevNarrowLabel, dayLabel) {
		if (
			prevNarrowLabel &&
			dayLabel.date.getDate() === narrowLabel.date.getDate() &&
			dayLabel.date.getDate() !== prevNarrowLabel.date.getDate()
		) {
			return true;
		}

		return false;
	}

	/**
	 * Render the horizontal grid lines and optionally the horizontal normalize line
	 * @param {number[]} yTicks - Array of the yAxis tick locations
	 */
	renderHorizontalGrid(yTicks) {
		const { panel } = this;
		const { style } = panel.params;
		const ctx = panel.rootContext;

		// regular grid lines and ticks
		this.renderHorizontalGridLines(ctx, yTicks);
		this.renderYAxisTicks(ctx, yTicks);

		// optional horizontal normalization line (0%)
		if (
			panel.isUpper &&
			panel.isNormalized &&
			style.grid.horizontal.normalize.color !== null
		) {
			this.renderHorizontalNormalizationLine(ctx);
		}
	}

	/**
	 * Renders the yAxis ticks
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderYAxisTicks(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const { color, length, lineDash, width } = style.yAxis.tick;
		let x0;
		if (panel.core.params.yAxisSide === 'left') {
			x0 = panel.size.padding.left - length;
		} else {
			x0 = panel.size.padding.left + panel.size.width;
		}
		this.renderHorizontalLines(ctx, data, x0, length, color, lineDash, width);
	}

	/**
	 * Renders the horizontal normalization line at the 0% mark
	 * This allows the 0% line to be styled differently than the other grid lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 */
	renderHorizontalNormalizationLine(ctx) {
		const { panel } = this;
		const { style } = panel.params;
		const x0 = panel.size.padding.left;
		const data = [
			panel.yAxis.primaryScale(0.0)
		];
		const color =
			style.grid.horizontal.normalize.color ||
			style.grid.horizontal.color ||
			style.grid.color;
		const extension =
			style.grid.horizontal.normalize.extension ||
			style.grid.horizontal.extension ||
			style.grid.extension;
		const lineWidth =
			style.grid.horizontal.normalize.width ||
			style.grid.horizontal.width ||
			style.grid.width;
		const lineDash =
			style.grid.horizontal.normalize.lineDash ||
			style.grid.horizontal.lineDash ||
			style.grid.lineDash;
		// The extension will push the lines past the chart border into the YAxis region
		const length = panel.size.width + extension;

		this.renderHorizontalLines(ctx, data, x0, length, color, lineDash, lineWidth);
	}

	/**
	 * Renders the horizontal grid lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of Y-coordinates for each line
	 */
	renderHorizontalGridLines(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const x0 = panel.size.padding.left;
		const color =
			style.grid.horizontal.color ||
			style.grid.color;
		const extension =
			style.grid.horizontal.extension ||
			style.grid.extension;
		const lineDash =
			style.grid.horizontal.lineDash ||
			style.grid.lineDash;
		const lineWidth =
			style.grid.horizontal.width ||
			style.grid.width;
		// The extension will push the lines past the chart border
		const length = panel.size.width + extension;

		this.renderHorizontalLines(ctx, data, x0, length, color, lineDash, lineWidth);
	}

	/**
	 * Common render method for horizontal lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of Y-coordinates, one per desired line
	 * @param {number} x0 - Location of left edge of the lines
	 * @param {number} length - Horizontal length of lines to render
	 * @param {string} color - Color to use for these lines
	 * @param {string[]} lineDash - Settings to turn on a dashed line
	 * @param {number} lineWidth - Line width (i.e. stroke width) to use for these lines
	 */
	renderHorizontalLines(ctx, data, x0, length, color, lineDash, lineWidth) {
		const { panel } = this;
		const yMin = 0;
		const yMax = panel.size.height;
		if (
			lineWidth &&
			(
				color &&
				(
					color !== 'transparent' ||
					color !== 'none'
				)
			)
		) {
			ctx.save();
			ctx.beginPath();

			ctx.strokeStyle = color;
			ctx.lineWidth = lineWidth;
			ctx.setLineDash(lineDash.split(/\s*,\s*/));
			data.forEach(y0 => {
				if (
					y0 >= yMin &&
					y0 < yMax
				) {
					ctx.moveTo(x0, y0);
					ctx.lineTo(x0 + length, y0);
				}
			});
			ctx.stroke();
			ctx.restore();
		}
	}
}

export default Grid;
